import {
  useState, useEffect, useCallback, ReactNode,
} from 'react';
import { Tabs, Select, TabsProps } from 'antd';
import type { DefaultOptionType } from 'antd/es/select';
import { useTranslation } from 'react-i18next';
import { IconSvg } from 'shared/icon';
import { StyledTabItem, StyledTabItemBold } from 'components/styledComponents/tabs/Tabs.styled';
import { StyledIconDefault } from 'shared/icon/Icon.styled';
import { StyledUserManagementSelectWrapper } from 'components/styledComponents/select/Select.styled';

const { Option } = Select;

type AntdTabItem = NonNullable<TabsProps['items']>[number];

type Tab = AntdTabItem & {
  label: string;
  content: ReactNode;
  icon?: string;
  isAssigned?: boolean;
  hide?: boolean;
}

type ExtraContentPlacement = 'left' | 'right';

export interface VerticalTabsProps {
  position?: TabsProps['tabPlacement'];
  tabs: Tab[];
  tabChange: TabsProps['onChange'];
  activeKey?: TabsProps['activeKey'];
  initial?: boolean;
  setInitial?: (value: boolean) => void;
  destroyOnHidden?: TabsProps['destroyOnHidden'];
  extraContentPlacement?: ExtraContentPlacement;
}

const VerticalTabs = ({
  position = 'start', tabs, tabChange, activeKey, initial = false, setInitial, destroyOnHidden = false, extraContentPlacement = 'left',
}: VerticalTabsProps) => {
  const { t } = useTranslation();

  const [selectedIds, setSelectedIds] = useState<string[]>([]);
  const [data, setData] = useState<Tab[]>(tabs);
  const [key, setKey] = useState<string>();

  useEffect(() => {
    if (activeKey) {
      setKey(activeKey);
    }
  }, [activeKey]);

  const handleTabChange = useCallback(
    (actKey: string) => {
      setKey(actKey);
      if (tabChange) {
        tabChange(actKey);
      }
    },
    [tabChange],
  );

  // if we start to select keys, set 0 item as active key. If we start to clear selectedIds set 0 item as active
  useEffect(() => {
    if (selectedIds.length === 1) {
      handleTabChange(data[0].key);
    } else if (!selectedIds.length && data.length < tabs.length) {
      handleTabChange(tabs[0].key);
    }
  }, [data, handleTabChange, selectedIds.length, tabChange, tabs]);

  useEffect(() => {
    if (initial) {
      setSelectedIds([]);
      handleTabChange(tabs[0].key);
      setInitial?.(false);
    }
  }, [handleTabChange, initial, setInitial, tabs]);

  useEffect(() => {
    let tempData = tabs;
    if (selectedIds.length) {
      tempData = tabs.filter((el) => selectedIds.includes(el.key)).sort((a, b) => selectedIds.indexOf(a.key) - selectedIds.indexOf(b.key));
    }
    setData(tempData);
  }, [selectedIds, tabs]);

  const handleChange = (value: string[]) => {
    setSelectedIds(value);
  };

  const tabsOptions = () => tabs.map((el) => (
    <Option key={el.key} value={el.key}>
      {el.label}
    </Option>
  ));

  const handleDeselect = (value: string) => {
    if (key === value) {
      const firstAvailable = data.find((el) => el.key !== value);
      if (firstAvailable) {
        handleTabChange(firstAvailable.key);
      }
    }
  };

  const tabBarExtraContent = {
    [extraContentPlacement]: (
      <StyledUserManagementSelectWrapper
        allowClear
        mode="multiple"
        classNames={{
          popup: {
            root: 'multi-select',
          },
        }}
        placeholder={t('all')}
        value={selectedIds}
        onChange={(value) => handleChange(value as string[])}
        onDeselect={(value) => handleDeselect(value as string)}
        maxTagCount={1}
        suffixIcon={(
          <StyledIconDefault>
            <IconSvg icon="DropdownIcon" width="0.7rem" />
          </StyledIconDefault>
        )}
        showSearch={{
          filterOption: (input, option?: DefaultOptionType) => {
            if (!option) return false;

            const label = (option?.children ?? '').toString().toLowerCase();

            return label.startsWith(input.toLowerCase());
          },
        }}
      >
        {tabsOptions()}
      </StyledUserManagementSelectWrapper>
    ),
  };

  const renderTabBar: TabsProps['renderTabBar'] = (passProps, DefaultTabBar) => <DefaultTabBar {...passProps} />;
  const tabItems = data.filter((pane) => !pane.hide).map(
    (pane) => ({
      key: pane.key,
      label: (
        <StyledTabItem $paneIcon={!!pane.icon}>
          <StyledTabItemBold $isAssigned={pane.isAssigned} title={pane.label}>
            {pane.label}
          </StyledTabItemBold>
        </StyledTabItem>
      ),
      children: pane.content,
    }
    ),
  );

  return (
    <Tabs
      tabBarExtraContent={tabs.length ? tabBarExtraContent : null}
      onChange={handleTabChange}
      tabPlacement={position}
      renderTabBar={renderTabBar}
      defaultActiveKey={data?.[0]?.key || undefined}
      activeKey={key}
      destroyOnHidden={destroyOnHidden}
      items={tabItems}
    />
  );
};

export default VerticalTabs;
